import React from 'react';
import { ContentBlock, ContentBlockProps } from '@jbhi-fi/components/src/ContentBlock';
import { PresentationProductCarousel, ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { ContentBanner } from '@jbhi-fi/components/src/ContentBanner/ContentBanner';
import { PresentationVideoCardCarousel } from '@jbhi-fi/components/src/ContentCard/VideoCardCarousel/VideoCardCarousel';
import { mergeResponsiveValues, ResponsiveValue } from '@jbhi-fi/utils/src/responsiveValue/responsiveValue';
import { CardProvider } from '../hooks/useCards';
import { CardGrid } from '@jbhi-fi/components/src/ContentCard';
import { CardGridVariant } from '@jbhi-fi/components/src/ContentCard/CardGrid/CardGrid';
import { HeroBlock } from '@jbhi-fi/components/src/HeroBlock';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { HeadingDivider, HeadingDividerVariant } from '@jbhi-fi/components/src/HeadingDivider/HeadingDivider';
import { useResponsiveValue } from '@jbhi-fi/components/src/hooks/useResponsiveValue';
import { styles } from './Section.css';
import { spacing } from '@jbhi-fi/lanyard-ui/themes';
import { ContentTheme } from '@jbhi-fi/components/src/Contentful/transformers/ThemeTransformer';
import { ListingBlock } from '@jbhi-fi/components/src/ListingBlock';
import { TabsBlock } from '@jbhi-fi/components/src/TabsBlock/TabsBlock';
import { MultiColumnBlock } from '@jbhi-fi/components/src/MultiColumnBlock/MultiColumnBlock';
import { SideBySide } from '@jbhi-fi/components/src/SideBySide/SideBySide';
import { overrideElementTheme } from './overrideElementTheme';
import { headingConstants } from './consts/headingConstants';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { CarouselLeadImage } from '../CarouselLeadImage/CarouselLeadImage';
import { ProductGrid } from '../ProductGrid/ProductGrid';
import { HEADING_FONT_VARIANTS, HeadingFontVariant } from '../HeadingDivider/HeadingFont';

export enum ElementType {
  cardProvider = 'cardProvider',
  cardGrid = 'cardGrid',
  cardGridWithPrimary = 'cardGridWithPrimary',
  heroBlock = 'heroBlock',
  productCarousel = 'productCarousel',
  carouselLeadImage = 'carouselLeadImage',
  countPresentationProductCarousel = 'countPresentationProductCarousel',
  presentationProductCarousel = 'presentationProductCarousel',
  presentationVideoCarousel = 'presentationVideoCarousel',
  cardCarousel = 'cardCarousel',
  contentBanner = 'contentBanner',
  listingBlock = 'listingBlock',
  productBlock = 'productBlock',
  multiColumnBlock = 'multiColumnBlock',
  sideBySide = 'sideBySide',
  promoCardCarousel = 'promoCardCarousel',
  productGrid = 'productGrid',
}
const ElementsWithTheme = [
  ElementType.contentBanner,
  ElementType.listingBlock,
  ElementType.productBlock,
  ElementType.multiColumnBlock,
  ElementType.productGrid,
];
export type SectionElementProps<T> = {
  type: ElementType;
  props: T;
};

export type SectionProps = {
  title?: string;
  subText?: string;
  href?: string;
  headingStyle?: 'Left' | 'Centred' | 'Banner' | 'Hidden';
  shouldOpenInNewWindow?: boolean;
  spaceBetweenElements?: Partial<ResponsiveValue<number>> | number;
  titlePadding?: Partial<ResponsiveValue<number>> | number;
  titleColor?: 'white' | 'black';
  titleVariant?: HeadingDividerVariant;
  contentBlockProps?: Omit<ContentBlockProps, 'children'>;
  elements: Array<SectionElementProps<any>>;
  contentPalette?: ContentTheme['palette'];
  isShowcase?: boolean;
};

const elementMap: Record<
  ElementType,
  { getComponent: () => React.FunctionComponent<any>; defaultProps: { [key: string]: any } }
> = {
  [ElementType.cardProvider]: { getComponent: () => CardProvider, defaultProps: {} },
  [ElementType.cardGrid]: {
    getComponent: () => CardGrid,
    defaultProps: { variant: CardGridVariant.default, shouldShowMobileCarousel: true },
  },
  [ElementType.cardGridWithPrimary]: {
    getComponent: () => CardGrid,
    defaultProps: { variant: CardGridVariant.withPrimary },
  },
  [ElementType.heroBlock]: { getComponent: () => HeroBlock, defaultProps: {} },
  [ElementType.productCarousel]: { getComponent: () => ProductCarousel, defaultProps: {} },
  [ElementType.carouselLeadImage]: { getComponent: () => CarouselLeadImage, defaultProps: {} },
  [ElementType.countPresentationProductCarousel]: {
    getComponent: () => PresentationProductCarousel,
    defaultProps: {},
  },
  [ElementType.presentationProductCarousel]: {
    getComponent: () => PresentationProductCarousel,
    defaultProps: {},
  },
  [ElementType.presentationVideoCarousel]: { getComponent: () => PresentationVideoCardCarousel, defaultProps: {} },
  [ElementType.cardCarousel]: {
    getComponent: () => Carousel,
    defaultProps: {
      fullHeightItems: true,
      showProgressBar: true,
      showArrows: true,
      spaceBetween: 10,
    },
  },
  [ElementType.contentBanner]: { getComponent: () => ContentBanner, defaultProps: {} },
  [ElementType.listingBlock]: { getComponent: () => ListingBlock, defaultProps: {} },
  [ElementType.productBlock]: {
    getComponent: () => TabsBlock,
    defaultProps: {},
  },
  [ElementType.multiColumnBlock]: {
    getComponent: () => MultiColumnBlock,
    defaultProps: {},
  },
  [ElementType.sideBySide]: {
    getComponent: () => SideBySide,
    defaultProps: {},
  },
  [ElementType.promoCardCarousel]: {
    getComponent: () => ListingBlock,
    defaultProps: {},
  },
  [ElementType.productGrid]: {
    getComponent: () => ProductGrid,
    defaultProps: {},
  },
};
const DEFAULT_CONTENT_BLOCK_PROPS: Partial<ContentBlockProps> = {
  paddingTop: { mobile: 4, tablet: 7.5 },
  paddingBottom: { mobile: 4, tablet: 7.5 },
};

const getHeadingContent = (title: string | undefined): React.ReactNode => {
  const hasHighlightedHeading = headingConstants.find(
    heading => heading.text?.trim()?.toLowerCase() === title?.trim()?.toLowerCase()
  );
  const { highlightedText } = hasHighlightedHeading ?? {};

  if (!title || !highlightedText) return title;
  const regex = new RegExp(`(${highlightedText})`, 'i');
  const parts = title.split(regex);
  return parts.map((part, idx) => {
    const isHighlight = regex.test(part);
    return isHighlight ? (
      <span key={idx}>{part}</span>
    ) : (
      <span key={idx} className={styles.scaledDownText}>
        {part}
      </span>
    );
  });
};

export const Section = ({
  title,
  href,
  shouldOpenInNewWindow,
  spaceBetweenElements,
  titlePadding,
  titleVariant,
  titleColor,
  contentBlockProps = {},
  elements,
  contentPalette,
  headingStyle,
  subText,
  isShowcase = false,
}: SectionProps) => {
  const titlePaddingResponsive = mergeResponsiveValues<number>({ desktopLg: 9, desktop: 9, mobile: 5 }, titlePadding);
  const titlePaddingFinal = useResponsiveValue<number>({
    xs: titlePaddingResponsive.mobile,
    md: titlePaddingResponsive.desktop,
    lg: titlePaddingResponsive.desktopLg,
  });
  const isDesktop = useBreakpoint('md', 'up');

  const spaceBetweenElementsResponsive = mergeResponsiveValues<number>(
    { desktopLg: 10, desktop: 10, mobile: 7.5 },
    spaceBetweenElements
  );
  const spaceBetweenElementsFinal = useResponsiveValue<number>({
    xs: spaceBetweenElementsResponsive.mobile,
    md: spaceBetweenElementsResponsive.desktop,
    lg: spaceBetweenElementsResponsive.desktopLg,
  });

  if (!elements?.length) return null;

  const headingContent = getHeadingContent(title);
  const isBanner = headingStyle === 'Banner';
  const marginTopOverride = isBanner ? (isDesktop ? 20 : 7.5) : undefined;
  const SubTextWrapper = contentPalette?.headingFontVariant === HEADING_FONT_VARIANTS.Crazy ? CalloutFont : 'div';

  return (
    <ContentBlock
      {...DEFAULT_CONTENT_BLOCK_PROPS}
      {...contentBlockProps}
      maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
      style={{
        backgroundColor: contentPalette?.background,
        marginTop: marginTopOverride ? spacing(marginTopOverride) : undefined,
      }}
    >
      {title ? (
        <>
          <div
            className={styles.headingContainer({ headingVariant: headingStyle ?? 'Left', isShowcase: isShowcase })}
            style={{
              paddingBottom: spacing(titlePaddingFinal),
            }}
          >
            <HeadingDivider
              heading={headingContent}
              href={href}
              shouldOpenInNewWindow={shouldOpenInNewWindow}
              textColor={contentPalette?.heading ? contentPalette.heading : (contentPalette?.text ?? titleColor)}
              variant={titleVariant}
              className={styles.heading({
                isBanner: isBanner,
                isShowcase: isShowcase,
                isCrazyFont: contentPalette?.headingFontVariant === HEADING_FONT_VARIANTS.Crazy,
              })}
              style={
                isBanner
                  ? {
                      backgroundColor: contentPalette?.headingBackground,
                    }
                  : undefined
              }
              headingFontVariant={contentPalette?.headingFontVariant as HeadingFontVariant}
            />
          </div>
          {subText ? (
            <SubTextWrapper
              className={styles.subtext({
                headingVariant: headingStyle ?? 'Left',
                isCrazyFont: contentPalette?.headingFontVariant === HEADING_FONT_VARIANTS.Crazy,
              })}
              style={{ color: contentPalette?.text }}
            >
              {subText}
            </SubTextWrapper>
          ) : null}
        </>
      ) : null}
      <div className={styles.elementsContainer} style={{ gap: spacing(spaceBetweenElementsFinal) }}>
        {elements.map((element, i) => {
          const { getComponent, defaultProps } = elementMap[element.type];
          let updatedProps = element.props;
          if (contentPalette && ElementsWithTheme.includes(element.type)) {
            updatedProps = overrideElementTheme(element.type, element.props, contentPalette);
          }
          const Component = getComponent();
          return <Component key={i} {...defaultProps} {...updatedProps} />;
        })}
      </div>
    </ContentBlock>
  );
};
